/**
 * The brand spelling of a storage provider type and the country of an Amazon marketplace. Brand logos and names come
 * from the API (logo_url, brand_name; connections-db storage_provider_brands); a provider without a logo keeps its
 * country flag or the warehouse mark (ProviderMark).
 */

/** Brand spellings the generic type name gets wrong ("threeplify" -> "Threeplify"). */
const PROVIDER_NAMES: Record<string, string> = {
  threeplify: '3PLify',
  '3plify': '3PLify',
};

export const providerBrandName = (provider?: string): string | undefined =>
  provider ? PROVIDER_NAMES[provider.toLowerCase()] : undefined;

/** Amazon marketplace id -> ISO 3166-1 alpha-2 (SP-API marketplace ids). */
const AMAZON_MARKETPLACE_COUNTRY: Record<string, string> = {
  ATVPDKIKX0DER: 'US',
  A2EUQ1WTGCTBG2: 'CA',
  A1AM78C64UM0Y8: 'MX',
  A2Q3Y263D00KWC: 'BR',
  A1F83G8C2ARO7P: 'GB',
  A1PA6795UKMFR9: 'DE',
  A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT',
  A1RKKUPIHCS9HS: 'ES',
  A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
  A1C3SOZRARQ6R3: 'PL',
  A33AVAJ2PDY3EV: 'TR',
  AMEN7PMS3EDWL: 'BE',
  A2VIGQ35RCS4UG: 'AE',
  A17E79C6D8DWNP: 'SA',
  ARBP9OOSHTCHU: 'EG',
  A21TJRUUN4KGV: 'IN',
  A1VC38T7YXB528: 'JP',
  A39IBJ37TRP1C6: 'AU',
  A19VAU5U5O7RUS: 'SG',
};

export const marketplaceCountry = (marketplaceId?: string | null) =>
  marketplaceId ? AMAZON_MARKETPLACE_COUNTRY[marketplaceId] : undefined;

const ISO_COUNTRY = /^[A-Z]{2}$/;

/**
 * The countries a provider stores in, from its warehouses' region (inventory.locations.region: an ISO alpha-2 code
 * set from the marketplace connection; a marketplace id is mapped too). Distinct, in order; unknown regions are dropped.
 */
export const providerCountries = (
  warehouses: ReadonlyArray<{ region: string | null }>,
): string[] => {
  const seen = new Set<string>();
  for (const { region } of warehouses) {
    const code = marketplaceCountry(region) ?? region?.toUpperCase();
    if (code && ISO_COUNTRY.test(code)) seen.add(code);
  }
  return [...seen];
};

/** The Amazon region a node name ends with, "Amazon FBA (Amazon North America)": the flag says it instead. */
const AMAZON_REGION_SUFFIX = /\s*\(Amazon (North America|Europe|Far East)\)$/;

export const withoutAmazonRegion = (name: string) =>
  name.replace(AMAZON_REGION_SUFFIX, '');
